import { motion } from 'framer-motion'
import { ShieldCheck, ShieldAlert, HardDrive } from 'lucide-react'
import { PageMeta } from '../components/seo/PageMeta'
import { Button } from '../components/ui/Button'
import { SectionLabel } from '../components/ui/SectionLabel'
import { pageMeta } from '../lib/seo'
import { securitySchema } from '../lib/schema'

const SPECTRA_TEAL = '#0d7a75'

const certCards = [
  {
    icon: ShieldCheck,
    title: 'ENDPOINT SECURITY',
    body: 'Certified protection against ransomware. If a ransomware attack succeeds and the service fails to contain it within the warranty terms, SPECTRA refunds you directly.',
    badge: '/badges/spectra-endpoint.png',
    alt: 'SPECTRA Certified Endpoint Security',
  },
  {
    icon: ShieldAlert,
    title: 'BEC PROTECTION',
    body: 'Certified protection against Business Email Compromise leading to fund transfer fraud or data loss. The #1 cause of cyber-related financial loss at SMBs. Backed by a $10,000 SPECTRA warranty — SPECTRA refunds you directly if the service fails.',
    badge: '/badges/spectra-bec.png',
    alt: 'SPECTRA Certified BEC Protection',
  },
  {
    icon: HardDrive,
    title: 'BACKUP',
    body: 'Certified backup resilience against any disaster scenario. If your backup fails to recover your data when needed, SPECTRA refunds you directly.',
    badge: '/badges/spectra-backup.png',
    alt: 'SPECTRA Certified Backup',
  },
]

export default function Security() {
  return (
    <>
      <PageMeta
        title={pageMeta.security.title}
        description={pageMeta.security.description}
        canonical={pageMeta.security.canonical}
        schema={securitySchema}
      />
      <main className="min-h-screen">
        {/* HERO */}
        <section
          style={{
            background: 'var(--bg)',
            padding: '160px 0 120px',
          }}
        >
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px', textAlign: 'center' }}>
            <SectionLabel>SPECTRA CERTIFIED PARTNER</SectionLabel>
            <h1
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(48px, 6vw, 72px)',
                textTransform: 'uppercase',
                lineHeight: 1.05,
                color: 'var(--white)',
                marginBottom: 28,
              }}
            >
              {'Your IT provider should be able to '}
              <span style={{ color: 'var(--accent)' }}>prove</span>
              {' your security. Not just promise it.'}
            </h1>
            <p
              style={{
                fontSize: 17,
                color: 'var(--white-dim)',
                lineHeight: 1.75,
                marginBottom: 40,
              }}
            >
              NextTech Consultants is a SPECTRA Certified Partner — the only certification program recognized by leading cyber insurance brokers and insurers. Qualifying clients receive three independent certifications covering ransomware protection, email fraud protection, and backup resilience — each backed by a $10,000 SPECTRA warranty — $30,000 in combined coverage. If the service fails during a covered event, SPECTRA refunds you directly. Not a promise from us. A guarantee from a third party.
            </p>
            <Button to="/contact" variant="primary" className="mb-12">
              Book a Free Security Assessment Call
            </Button>
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 24,
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <picture><source srcSet="/badges/spectra-partner.webp" type="image/webp" /><img src="/badges/spectra-partner.png" alt="SPECTRA Certified Partner" width={48} height={48} style={{ height: 48, width: 48, display: 'block' }} /></picture>
              <picture><source srcSet="/badges/spectra-endpoint.webp" type="image/webp" /><img src="/badges/spectra-endpoint.png" alt="SPECTRA Endpoint Security" width={48} height={48} style={{ height: 48, width: 48, display: 'block' }} /></picture>
              <picture><source srcSet="/badges/spectra-bec.webp" type="image/webp" /><img src="/badges/spectra-bec.png" alt="SPECTRA BEC Protection" width={48} height={48} style={{ height: 48, width: 48, display: 'block' }} /></picture>
              <picture><source srcSet="/badges/spectra-backup.webp" type="image/webp" /><img src="/badges/spectra-backup.png" alt="SPECTRA Backup" width={48} height={48} style={{ height: 48, width: 48, display: 'block' }} /></picture>
            </div>
          </div>
        </section>

        {/* WHAT SPECTRA MEANS */}
        <section style={{ background: 'var(--bg-2)', padding: '120px 0' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px' }}>
            <SectionLabel>THE CERTIFICATION</SectionLabel>
            <h2
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(38px, 5vw, 56px)',
                textTransform: 'uppercase',
                lineHeight: 1.05,
                color: 'var(--white)',
                marginBottom: 24,
              }}
            >
              Third-party verified. Performance-warranted. Insurance-ready.
            </h2>
            <p
              style={{
                fontSize: 16,
                color: 'var(--white-dim)',
                lineHeight: 1.75,
                marginBottom: 56,
                maxWidth: 800,
              }}
            >
              SPECTRA is an independent cybersecurity certification organization. As a SPECTRA Certified Partner, NextTech Consultants has met their standards for security service delivery across three areas. Qualifying clients receive three separate Certificates of Resilience — Endpoint Security, BEC Protection, and Backup — each independently assessed and each carrying a $10,000 warranty — $30,000 combined. If a covered cyber event occurs and the service fails to perform, SPECTRA refunds the client directly.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-[3px]" style={{ marginBottom: 3 }}>
              {certCards.map((card, i) => (
                <motion.div
                  key={card.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.1 }}
                  className="security-cert-card relative overflow-hidden transition-colors duration-250"
                  style={{
                    background: 'var(--bg-3)',
                    padding: '40px 32px',
                  }}
                >
                  <card.icon
                    style={{ color: SPECTRA_TEAL, width: 28, height: 28, marginBottom: 16 }}
                  />
                  <h3
                    style={{
                      fontFamily: 'Barlow Condensed, sans-serif',
                      fontWeight: 800,
                      fontSize: 20,
                      textTransform: 'uppercase',
                      color: SPECTRA_TEAL,
                      marginBottom: 12,
                    }}
                  >
                    {card.title}
                  </h3>
                  <p style={{ fontSize: 14, lineHeight: 1.7, color: 'var(--white-dim)', marginBottom: 20 }}>
                    {card.body}
                  </p>
                  <picture><source srcSet={card.badge.replace('.png', '.webp')} type="image/webp" /><img src={card.badge} alt={card.alt} width={40} height={40} style={{ height: 40, width: 40, display: 'block' }} /></picture>
                </motion.div>
              ))}
            </div>
            <style dangerouslySetInnerHTML={{ __html: `
              .security-cert-card:hover { background: var(--bg-4) !important; }
              .security-cert-card::after {
                content: "";
                position: absolute;
                bottom: 0; left: 0;
                width: 100%; height: 2px;
                background: var(--accent);
                transform: scaleX(0);
                transform-origin: left;
                transition: transform 0.3s ease;
              }
              .security-cert-card:hover::after { transform: scaleX(1); }
            ` }} />

            <div
              style={{
                marginTop: 3,
                background: 'var(--accent-glow)',
                border: '1px solid var(--accent-line)',
                padding: '28px 36px',
                marginBottom: 40,
              }}
            >
              <h3
                style={{
                  fontFamily: 'Barlow Condensed, sans-serif',
                  fontWeight: 900,
                  fontSize: 28,
                  color: 'var(--accent)',
                  marginBottom: 10,
                  lineHeight: 1.1,
                }}
              >
                Each warranty is valued at $10,000 — $30,000 in combined coverage across all three certifications.
              </h3>
              <p style={{ fontSize: 14, color: 'var(--white-dim)' }}>
                In the event of a covered incident, SPECTRA handles the refund directly to you.
              </p>
            </div>

            <p style={{ fontSize: 15, color: 'var(--white-dim)', lineHeight: 1.75, marginBottom: 48 }}>
              As a SPECTRA Certified MSP, NextTech Consultants can connect you with SPECTRA-approved cyber insurance brokers for preferential coverage based on your verified security posture. SPECTRA certification replaces lengthy insurance questionnaires — clients are typically quoted within 2–3 business days.
            </p>

            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: 28,
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 18,
              }}
            >
              <picture><source srcSet="/badges/spectra-partner.webp" type="image/webp" /><img src="/badges/spectra-partner.png" alt="SPECTRA Certified Partner" width={64} height={64} style={{ height: 64, width: 64, display: 'block' }} /></picture>
              <picture><source srcSet="/badges/spectra-endpoint.webp" type="image/webp" /><img src="/badges/spectra-endpoint.png" alt="SPECTRA Endpoint Security" width={64} height={64} style={{ height: 64, width: 64, display: 'block' }} /></picture>
              <picture><source srcSet="/badges/spectra-bec.webp" type="image/webp" /><img src="/badges/spectra-bec.png" alt="SPECTRA BEC Protection" width={64} height={64} style={{ height: 64, width: 64, display: 'block' }} /></picture>
              <picture><source srcSet="/badges/spectra-backup.webp" type="image/webp" /><img src="/badges/spectra-backup.png" alt="SPECTRA Backup" width={64} height={64} style={{ height: 64, width: 64, display: 'block' }} /></picture>
            </div>
            <p style={{ fontSize: 13, color: 'var(--white-dim)', textAlign: 'center' }}>
              NextTech Consultants is a SPECTRA Certified Partner. Qualifying clients receive all three service certifications.
            </p>
          </div>
        </section>

        {/* WHO GETS CERTIFIED */}
        <section style={{ background: 'var(--bg)', padding: '120px 0' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px' }}>
            <SectionLabel>INCLUDED IN YOUR PLAN</SectionLabel>
            <h2
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(36px, 4.5vw, 52px)',
                textTransform: 'uppercase',
                lineHeight: 1.05,
                color: 'var(--white)',
                marginBottom: 24,
              }}
            >
              Included for NextTech Consultants clients with 12 or more employees.
            </h2>
            <p
              style={{
                fontSize: 16,
                color: 'var(--white-dim)',
                lineHeight: 1.75,
                marginBottom: 28,
              }}
            >
              All three SPECTRA certifications — Endpoint Security, BEC Protection, and Backup — are included as part of NextTech Consultants' managed IT plan for teams of 12 or more. No add-on cost. No separate engagement. You receive all three certificates and $30,000 in combined warranty coverage as part of being a NextTech Consultants client.
            </p>
            <p style={{ fontSize: 15, color: 'var(--white-dim)', lineHeight: 1.7 }}>
              Particularly valuable for: Legal & Professional Services · Healthcare & Medical Practices · Nonprofits handling sensitive data · Any business that handles client financial or health information
            </p>
          </div>
        </section>

        {/* WHAT THE CERTIFICATES COVER */}
        <section style={{ background: 'var(--bg-2)', padding: '120px 0' }}>
          <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 48px' }}>
            <SectionLabel>THE CERTIFICATES</SectionLabel>
            <h2
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(36px, 4.5vw, 52px)',
                textTransform: 'uppercase',
                lineHeight: 1.05,
                color: 'var(--white)',
                marginBottom: 24,
              }}
            >
              Three certificates. Three warranties. Proof you can hand to a client, auditor, or partner.
            </h2>
            <p style={{ fontSize: 16, color: 'var(--white-dim)', lineHeight: 1.75 }}>
              Each Certificate of Resilience documents that NextTech Consultants has designed and implemented that service with the appropriate controls to ensure consistent and secure service delivery during a covered cyber event. All three are issued by SPECTRA, signed by their President of Resilient Services, include a unique registration number and expiration date — and are fully verifiable.
            </p>
          </div>
        </section>

        {/* CTA */}
        <section
          style={{
            background: 'var(--bg)',
            padding: '120px 0',
            textAlign: 'center',
          }}
        >
          <div style={{ maxWidth: 720, margin: '0 auto', padding: '0 48px' }}>
            <h2
              style={{
                fontFamily: 'Barlow Condensed, sans-serif',
                fontWeight: 900,
                fontSize: 'clamp(32px, 4vw, 44px)',
                textTransform: 'uppercase',
                lineHeight: 1.1,
                color: 'var(--white)',
                marginBottom: 20,
              }}
            >
              Want to see what a certified security environment looks like for your business?
            </h2>
            <p
              style={{
                fontSize: 16,
                color: 'var(--white-dim)',
                lineHeight: 1.7,
                marginBottom: 32,
              }}
            >
              Book a free 30-minute security assessment call. We'll walk through your current setup, identify your biggest exposure points, and show you exactly what SPECTRA certification would cover for your team.
            </p>
            <Button to="/contact" variant="primary">
              Book a Free Security Assessment Call
            </Button>
          </div>
        </section>
      </main>
    </>
  )
}
export { Security as Component }
